package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

private val CARD_FINE_POINTER_QUERY = CSSMediaQuery.Raw("(hover: hover) and (pointer: fine)")

val LCardStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(colorMode.toSitePalette().surface)
            .color(colorMode.toSitePalette().textPrimary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .borderRadius(Radius.Card)
            .padding(Space.LG)
            .interactiveTransition()
    }
    // Cards that are themselves links or buttons lift under the cursor. A plain card does not,
    // because a surface that responds to hover while doing nothing on click is a false affordance.
    //
    // **This covers the card that *is* the control, and cannot cover the card wrapped in one.** The
    // second half used to be written `:is(a, button) > &:hover`, borrowing `&` from Sass. CSS has
    // its own `&` and it means something else: outside a nested rule it resolves to `:scope`, so
    // that selector asked for a link whose child is the document root, and matched nothing on any
    // page ever served. It was the only `&` in the design system, and it read as though the case
    // were handled.
    //
    // It cannot be handled here. Silk builds these rules by appending a suffix to the style's own
    // class, and a wrapped card needs a *prefix* — a condition on an ancestor — which no suffix can
    // express. A card that should lift therefore has to be the anchor or the button itself, which
    // is the better markup regardless: it puts the whole hit area in the tab order and gives the
    // reader one thing to click rather than a link inside a surface that also looks clickable.
    cssRule(CARD_FINE_POINTER_QUERY, ":is(a, button):hover") {
        Modifier
            // On paper, lift is a firmer edge plus the rise — not a cast shadow.
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .styleModifier { property("transform", "translateY(-${Motion.HoverLiftPx}px)") }
    }
}

@Composable
fun LCard(
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    Div(LCardStyle.toModifier().then(modifier).toAttrs()) {
        content()
    }
}

/**
 * The card that *is* the anchor, for a card whose whole surface goes somewhere.
 *
 * `LCard` renders a `<div>`; wrapping that in a `Link` gives a reader two things to aim at — the
 * card's edges, which do nothing, and a smaller `Link` somewhere inside it, which does — and it is
 * the case [LCardStyle]'s own hover-lift rule says it cannot cover: that rule fires on
 * `:is(a, button):hover` against the card's own class, and a `Link` nested a level down is not the
 * element carrying that class. There is no CSS this design system's `cssRule` can write that reaches
 * up from an inner anchor to lift an outer div — only the reverse, a suffix on the card's own
 * element. So the card itself has to be the anchor, which is also the better markup: the whole hit
 * area joins the tab order as one stop instead of a card-shaped decoration around a link-shaped one.
 *
 * Built on Kobweb's [Link] rather than a raw `A` so navigation stays client-side and respects
 * modifier-key clicks (open in new tab, etc.) the way every other link in the product does.
 * `LCardLinkStyle` layers `display: block`, `color: inherit` and no underline over [LCardStyle] the
 * same way [PlainLinkStyle] overrides Silk's own link colours elsewhere in the site — Silk's default
 * `:link`/`:visited` blue (cyan in dark) is not in either palette, and a whole card painted in it
 * would be the loudest thing on the page it sits on.
 *
 * The title should still say "this is a link" without colouring the whole tile — [LCardLinkStyle]
 * underlines on hover, but only an element the caller marks `data-card-title`, so a plain `Span` in
 * [content] carrying that attribute gets the affordance and the description beside it does not.
 */
@Composable
fun LCardLink(
    path: String,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    Link(
        path = path,
        modifier = LCardStyle.toModifier().then(LCardLinkStyle.toModifier()).then(modifier),
    ) {
        content()
    }
}

val LCardLinkStyle = CssStyle {
    base {
        Modifier
            .styleModifier { property("display", "block") }
            .textDecorationLine(TextDecorationLine.None)
            .styleModifier { property("color", "inherit") }
    }
    cssRule(":hover [data-card-title]") {
        Modifier.textDecorationLine(TextDecorationLine.Underline)
    }
    // Consistent with the rest of the kit's controls (see `LButtonStyle`, `LStatTileStyle`): a
    // visible ring on keyboard focus, offset so it reads as a halo around the card rather than a
    // line cutting through its own border.
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}
